import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CareExecutionReport,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCareExecutionReportColumns,
  useDynamicCallback,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useMemo } from 'react';

import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import { DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS } from '@talos/kyoko';
import type { GetMainMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useCareExecutionReportsFilter,
  type CareExecutionReportsTableFilter,
} from './useCareExecutionReportsFilter';
import { useCareExecutionReportsMenu } from './useCareExecutionReportsMenu';

export interface FilteredCareExecutionReportsParams {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: Column[];
  defaultFilter?: CareExecutionReportsTableFilter;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (params: RowDoubleClickedEvent) => void;
}

export function CareExecutionReportsBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: FilteredCareExecutionReportsParams) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const columns = useCareExecutionReportColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useCareExecutionReportsFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const menu = useCareExecutionReportsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const getExtraMainMenuItems = useDynamicCallback((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const columnsWithMenu = useMemo(() => [...columns, ...menu.columns], [columns, menu.columns]);

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: 'CareExecutionReport',
      sort_by: '-Timestamp',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, CareExecutionReport>({
    request,
    rowID: CareExecutionReport.rowID,
    clientLocalFilter,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: menu.getContextMenuItems,
    },
    exportOptions: {
      name: 'CareExecutionReports',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      <BlotterTable {...blotterTable} />
      {menu.dialogs}
    </>
  );
}
